import { Box, Button, Grid, Typography, Paper, Card, CircularProgress, Divider } from "@mui/material";
import { BankAccountResponse, PaymentContent, ProcessExecutionResponse, StepExecutionResponse } from "api/main/data-contracts";
import { useEffect, useRef, useState } from "react";
import { createApi } from "util/ApiCreator";
import { adminTheme } from "theme/adminTheme";
import PaymentIcon from "@mui/icons-material/Payment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface Props {
  process: ProcessExecutionResponse;
  step: StepExecutionResponse;
  execute: (obj: object) => Promise<void>;
  refresh: () => Promise<void>;
  previous: () => void;
  next: () => void;
  canGoNext: boolean;
  canGoBack: boolean;
}

export default function PaymentExecution({
  process,
  step,
  execute,
  refresh,
  next,
  previous,
  canGoBack, 
  canGoNext
}: Props) {

  const [bankAccount, setBankAccount] = useState<BankAccountResponse>();
  const paymentContent: PaymentContent = getPaymentContent();
  const bankAccountApi = useRef(createApi("bankaccount"));
  const kevinApi = useRef(createApi("payment"));
  const [loading, setLoading] = useState<boolean>(true);
  const [paymentStatus, setPaymentStatus] = useState<string>(step.executionInfo?.paymentInfo?.status ?? 'defaultStatus');
  const urlParams = new URLSearchParams(window.location.search);
  const paymentId = urlParams.get('paymentId');
  const status = urlParams.get('status');
  const statusGroup = urlParams.get('statusGroup');

  const fetchBankAccount = async () => {
    if (!paymentContent.bankAccountId)
      return;
    setBankAccount(await bankAccountApi.current.findById(paymentContent.bankAccountId));
  }

  const getPaymentStatus = async () => {
    if (!isUrlParamsCorrect()) {
      return;
    }
    step.id && paymentId && setPaymentStatus(await kevinApi.current.getPaymentStatus(step.id, paymentId));
    await refresh();
  }

  function isUrlParamsCorrect(): boolean {
    if (paymentId === null || statusGroup === null || status === null) {
      return false;
    }
    return true;
  };

  function getPaymentContent(): PaymentContent {
    return step.step?.stepInformation?.paymentContent ?? {
      amount: 0,
      description: "",
      bankAccountId: undefined,
    };
  }

  const isCompleted: boolean = paymentStatus === "completed";
  const isStepActive: boolean = step.status === "FINISHED";

  useEffect(() => {
    setLoading(true);
    getPaymentStatus();
    fetchBankAccount();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePaymentButtonClick = async () => {
    if (!process.id) {
      return;
    }
    if (!step.id) {
      return;
    }
    const response = await kevinApi.current.initializePayment(process?.id, step?.id);
    if (response) {
      window.location.replace(response);
    }
  }

  const handleExecuteButtonClick = async () => {
    if (paymentStatus !== "completed" && paymentStatus !== "pending") {
      return;
    }
    await execute({ paymentContent }).catch((err: any) => {
    });
    await refresh();
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 5 }}>
        <CircularProgress size={64} sx={{ color: adminTheme.colors.primary, mb: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
          Prašome palaukti
        </Typography>
        <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
          Kraunama...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
      {/* Payment Details Card */}
      <Card
        sx={{
          borderRadius: adminTheme.borderRadius.lg,
          border: `1px solid ${adminTheme.colors.border}`,
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${adminTheme.colors.primary}08 0%, ${adminTheme.colors.background} 100%)`,
            borderBottom: `1px solid ${adminTheme.colors.border}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: adminTheme.borderRadius.md,
              background: adminTheme.gradients.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <PaymentIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}>
              Mokėjimo detalės
            </Typography>
            <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
              Peržiūrėkite ir patvirtinkite mokėjimo informaciją
            </Typography>
          </Box>
          {isCompleted && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: `${adminTheme.colors.success}15`,
                borderRadius: adminTheme.borderRadius.md,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20, color: adminTheme.colors.success }} />
              <Typography variant="body2" sx={{ color: adminTheme.colors.success, fontWeight: 600 }}>
                Apmokėta
              </Typography>
            </Box>
          )}
        </Box>

        {/* Card Content */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Gavėjas
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary, mt: 0.5 }}>
                  {bankAccount?.creditorName}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Gavėjo sąskaitos numeris
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary, mt: 0.5, fontFamily: "monospace" }}>
                  {bankAccount?.creditorIban}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Suma
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: adminTheme.colors.primary, mt: 0.5 }}>
                  {paymentContent.amount} €
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Mokėjimo paskirtis
                </Typography>
                <Typography variant="body2" sx={{ color: adminTheme.colors.textPrimary, mt: 0.5 }}>
                  {paymentContent.description}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Error Message */}
          {paymentStatus === "failed" && (
            <Paper
              sx={{
                mt: 3,
                p: 2,
                bgcolor: `${adminTheme.colors.error}10`,
                border: `1px solid ${adminTheme.colors.error}40`,
                borderRadius: adminTheme.borderRadius.md,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <ErrorOutlineIcon sx={{ color: adminTheme.colors.error, fontSize: 24 }} />
              <Typography variant="body2" sx={{ color: adminTheme.colors.error, fontWeight: 600 }}>
                Nepavyko apmokėti. Bandykite dar kartą.
              </Typography>
            </Paper>
          )}

          {/* Payment Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handlePaymentButtonClick}
            disabled={isCompleted}
            sx={{
              mt: 3,
              background: adminTheme.gradients.primary,
              "&:hover": { background: adminTheme.gradients.primaryHover },
              py: 1.5,
              fontWeight: 600,
            }}
          >
            {isCompleted ? "Jau apmokėta" : "Apmokėti"}
          </Button>
        </Box>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
        <Button
          variant="outlined"
          size="large"
          disabled={isStepActive}
          onClick={() => handleExecuteButtonClick()}
          sx={{
            flex: 1,
            borderColor: adminTheme.colors.border,
            color: adminTheme.colors.textSecondary,
            py: 1.5,
            "&:hover": {
              borderColor: adminTheme.colors.primary,
              bgcolor: `${adminTheme.colors.primary}08`,
            },
          }}
        >
          Patvirtinti mokėjimą
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={!isCompleted}
          onClick={() => next()}
          sx={{
            flex: 1,
            background: adminTheme.gradients.success,
            "&:hover": { opacity: 0.9 },
            py: 1.5,
            fontWeight: 600,
          }}
        >
          Kitas
        </Button>
      </Box>
    </Box>
  );
}